/**
 * FilterDropdown Component
 *
 * Multi-select filter dropdown with configurable width.
 * Provides multi-select filtering with search, group support, and apply/clear actions.
 *
 * Key Features:
 * - Configurable dropdown width (fixes text truncation issues)
 * - Enhanced options logic (preserves selected values not in current options)
 * - Apply button state management (only enabled when changes made)
 * - Search filtering with selected items always visible
 * - Group selection with tri-state checkboxes
 * - Virtualization for large option lists
 * - Portal rendering with smart positioning
 * - Count-only display: Shows numeric count of selected items (e.g., "3") instead of labels
 * - Always-visible tooltip: Hover over count to see full list of selected items
 *
 * Display Pattern:
 * - Button shows: [Filter Label | 3 ▼]
 * - Tooltip shows full list of selected item labels on hover
 * - No label truncation needed - count is always compact
 *
 * Width Control Implementation:
 * - Width prop passed directly to DropdownList as inline style (line 514)
 * - Cannot use parent wrapper width due to Tailwind class specificity (w-[280px]/w-[400px])
 * - Inline style on DropdownList overrides internal Tailwind width classes
 * - fitWidth={true} makes option text expand, width prop controls dropdown container
 */

import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  NoSymbolIcon,
  PlusCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

import { DropdownList } from '@/components/legacy-ui/DropdownList';
import { Tooltip } from '@/components/Tooltip';
import { Skeleton } from '@/components/ui/skeleton';

import { FilterDropdownProps, FilterOption, GroupOption } from './types';
import {
  enforceMaxSelections,
  flattenOptions,
  isGroupOption,
  isValidMaxSelections,
  toggleGroupOption,
  toggleIndividualOption,
} from './utils';

/**
 * Helper function to find an option by value in the options structure
 * Searches both flat options and nested group items
 */
const findOptionByValue = (
  value: string,
  opts: (FilterOption | GroupOption)[]
): FilterOption | undefined => {
  for (const opt of opts) {
    if (opt.value === value) return opt;
    if ((opt as GroupOption).isGroup) {
      const groupOption = opt as GroupOption;
      const groupItems = groupOption.groupItems ?? groupOption.items;
      if (groupItems) {
        const found = findOptionByValue(value, groupItems as FilterOption[]);
        if (found) return found;
      }
    }
  }
  return undefined;
};

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  id,
  label,
  options,
  selectedValues,
  onApply,
  onClear,
  isLoadingOptions = false,
  isPraetorianUser = false,
  onDropdownStateChange,
  width = 400, // Default to existing 400px width
  isLoading = false,
  showSelectAll = true,
  showSearch = true,
  maxSelections,
  createOptionFromSearch,
  isNegated = false,
  onToggleNegation,
}) => {
  // State management
  const [isOpen, setIsOpenRaw] = useState(false);
  const [tempSelected, setTempSelected] = useState<FilterOption[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [buttonPosition, setButtonPosition] = useState<{
    bottom: number;
    left: number;
    width: number;
  }>();

  // Refs
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Runtime validation of maxSelections prop
  useEffect(() => {
    if (!isValidMaxSelections(maxSelections)) {
      console.error(
        `FilterDropdown [${id}]: maxSelections must be a positive integer, got:`,
        maxSelections
      );
    }
  }, [maxSelections, id]);

  /**
   * Enhanced Options Logic
   *
   * Critical feature: Include previously selected values that may not be in current options.
   * This prevents selected items from disappearing when API returns new filter options.
   *
   * Example: User selects "Amazon Registrar, Inc." → API updates options → "Amazon Registrar, Inc."
   * is no longer in the list but should still be visible as selected.
   *
   * In single-select mode (maxSelections === 1), flattens grouped options to remove group headers.
   */
  const enhancedOptions = useMemo(() => {
    const selectedOptionsNotInCurrent: FilterOption[] = [];

    // Find selected values that don't exist in current options
    for (const value of selectedValues) {
      const existsInCurrent = findOptionByValue(value, options);
      if (!existsInCurrent) {
        selectedOptionsNotInCurrent.push({
          id: value,
          label: value,
          value: value,
        });
      }
    }

    // Combine current options with any missing selected options
    let allOptions = [...options, ...selectedOptionsNotInCurrent];

    // Flatten groups in single-select mode (maxSelections === 1)
    if (maxSelections === 1) {
      allOptions = flattenOptions(allOptions);
    }

    return allOptions;
  }, [options, selectedValues, maxSelections]);

  // When the search box is hidden (`showSearch === false`), ignore any residual
  // `searchValue` so a stale filter can't keep narrowing the list invisibly —
  // e.g. if `showSearch` flips to false while the dropdown is open. Deriving
  // (rather than resetting state in an effect) keeps this in sync render-to-render
  // with no extra render.
  const effectiveSearchValue = showSearch ? searchValue : '';

  /**
   * Search Filtering Logic
   *
   * - Filters options based on search value
   * - Selected items always visible (even if they don't match search)
   * - Groups visible if any child matches search OR group label matches
   * - Groups with selected items always visible
   */
  const filteredOptions = useMemo(() => {
    if (!effectiveSearchValue) return enhancedOptions;

    // Create a set of selected values for quick lookup
    const selectedValuesSet = new Set(selectedValues);

    return enhancedOptions
      .map(option => {
        if ((option as GroupOption).isGroup && (option as GroupOption).items) {
          const groupOption = option as GroupOption;
          // Filter items in the group, but keep selected items regardless of search
          const filteredItems = groupOption.items!.filter(
            item =>
              item.label
                .toLowerCase()
                .includes(effectiveSearchValue.toLowerCase()) ||
              (item.value && selectedValuesSet.has(item.value.toString()))
          );

          // Return group with filtered items
          return {
            ...groupOption,
            items: filteredItems,
          } as GroupOption;
        }
        // For non-group options, just return as-is
        return option;
      })
      .filter(option => {
        if ((option as GroupOption).isGroup && (option as GroupOption).items) {
          const groupOption = option as GroupOption;
          // Keep group if it has matching items or its own label matches
          return (
            groupOption.items!.length > 0 ||
            option.label
              .toLowerCase()
              .includes(effectiveSearchValue.toLowerCase())
          );
        }
        // Keep non-group option if its label matches, it's selected, or it's a previously selected option
        return (
          option.label
            .toLowerCase()
            .includes(effectiveSearchValue.toLowerCase()) ||
          (option.value && selectedValuesSet.has(option.value.toString()))
        );
      });
  }, [enhancedOptions, effectiveSearchValue, selectedValues]);

  /**
   * Custom Search Option Logic
   *
   * When createOptionFromSearch is provided:
   * 1. If the search value produces a valid custom option not in the base options,
   *    prepend it to the filtered results.
   * 2. Preserve any temp-selected custom options (not yet applied) so they remain
   *    visible when the user changes the search to add more custom values.
   */
  const optionsWithCustom = useMemo(() => {
    if (!createOptionFromSearch) return filteredOptions;

    let result = [...filteredOptions];

    // Prepend custom option from current search value
    if (effectiveSearchValue) {
      const customOption = createOptionFromSearch(effectiveSearchValue.trim());
      if (customOption) {
        const customValue = customOption.value?.toString() ?? '';
        if (!findOptionByValue(customValue, options)) {
          result = [customOption, ...result];
        }
      }
    }

    // Preserve temp-selected custom options that aren't in the base options list.
    // This keeps previously checked custom values visible while the user searches
    // for additional ones within the same dropdown session.
    for (const item of tempSelected) {
      const value = item.value?.toString() ?? '';
      if (
        !findOptionByValue(value, options) &&
        !findOptionByValue(value, result)
      ) {
        result.push(item);
      }
    }

    return result;
  }, [
    filteredOptions,
    effectiveSearchValue,
    createOptionFromSearch,
    options,
    tempSelected,
  ]);

  /**
   * Selected Options Mapping
   *
   * Maps selectedValues (string[]) to FilterOption[] for display and processing
   * Deduplicates to avoid showing duplicate options
   */
  const selected = useMemo(() => {
    const selectedOptions: FilterOption[] = [];

    // Deduplicate selectedValues
    const uniqueSelectedValues = [...new Set(selectedValues)];

    // Process each selected value - all should be found in enhancedOptions
    for (const value of uniqueSelectedValues) {
      const option = findOptionByValue(value, enhancedOptions);
      if (option) {
        selectedOptions.push(option);
      }
    }

    return selectedOptions;
  }, [enhancedOptions, selectedValues]);

  /**
   * Dropdown Positioning with Configurable Width
   *
   * Uses the width prop for positioning calculations and dropdown sizing.
   * Prevents overflow on right/left edges of viewport.
   */
  const dropdownStyle = useMemo(() => {
    if (!isOpen || !buttonPosition) return undefined;

    const dropdownWidth = width; // Use prop directly
    const { bottom, left, width: buttonWidth } = buttonPosition;
    const screenWidth = window.innerWidth;

    // Calculate left position with overflow prevention
    let calculatedLeft = left;
    if (left + dropdownWidth > screenWidth) {
      // Try aligning dropdown's right edge with button's right edge
      calculatedLeft = left + buttonWidth - dropdownWidth;
      if (calculatedLeft < 0) {
        // Position dropdown's right edge with screen's right edge
        calculatedLeft = Math.max(0, screenWidth - dropdownWidth);
      }
    }
    // Final clamp: ensure dropdown is not off-screen to the left
    calculatedLeft = Math.max(0, calculatedLeft);

    return {
      top: `${bottom + 8}px`,
      left: `${calculatedLeft}px`,
      width: `${dropdownWidth}px`,
      pointerEvents: 'auto' as const,
    };
  }, [isOpen, buttonPosition, width]);

  /**
   * Wrapper around setIsOpenRaw that handles open/close side effects:
   * - Resets search value on close
   * - Clears button position on close
   * - Syncs temp selection on open
   * - Notifies parent about dropdown state change
   */
  const setIsOpen = useCallback(
    (nextOpen: boolean | ((prev: boolean) => boolean)) => {
      setIsOpenRaw(prev => {
        const resolved =
          typeof nextOpen === 'function' ? nextOpen(prev) : nextOpen;
        if (resolved === prev) return prev;

        if (resolved) {
          // Opening: sync temp selection with current selected
          setTempSelected(selected);
        } else {
          // Closing: reset search and clear position
          setSearchValue('');
          setButtonPosition(undefined);
        }

        if (onDropdownStateChange) {
          onDropdownStateChange(resolved, id);
        }

        return resolved;
      });
    },
    [selected, onDropdownStateChange, id]
  );

  /**
   * Update button position when dropdown opens or window resizes/scrolls
   */
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      // Functional setState + shallow equality bail-out: `scroll` fires
      // continuously on wheel/inertia, and a fresh object literal every tick
      // would rerender the dropdown at 60fps. Only update when the coords
      // that actually drive positioning have changed.
      setButtonPosition(prev =>
        prev &&
        prev.bottom === rect.bottom &&
        prev.left === rect.left &&
        prev.width === rect.width
          ? prev
          : { bottom: rect.bottom, left: rect.left, width: rect.width }
      );
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  /**
   * Handle click outside to close dropdown
   * Reverts tempSelected to original selected state (cancels changes)
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isOpen, setIsOpen]);

  /**
   * Keep the panel's list scrollable inside a Radix modal Dialog.
   *
   * Radix's modal Dialog wraps its content in react-remove-scroll, which adds a
   * bubble-phase `wheel`/`touchmove` listener on `document` that cancels scroll
   * for any target outside the DialogContent subtree. This panel is portalled to
   * document.body (a sibling of DialogContent), so without this its internal list
   * can't be scrolled while a Dialog host is open. Stopping wheel/touch events
   * from bubbling to `document` means that listener never fires, so the browser's
   * native scroll of the overflow-auto list proceeds. Harmless outside a Dialog.
   *
   * Must be a native listener: React's synthetic stopPropagation does not stop
   * the native event from reaching document's native listener.
   */
  useEffect(() => {
    if (!isOpen) return;
    const node = dropdownRef.current;
    if (!node) return;
    const stopPropagation = (event: Event) => event.stopPropagation();
    node.addEventListener('wheel', stopPropagation);
    node.addEventListener('touchmove', stopPropagation);
    return () => {
      node.removeEventListener('wheel', stopPropagation);
      node.removeEventListener('touchmove', stopPropagation);
    };
  }, [isOpen, dropdownStyle]);

  /**
   * Handle checkbox toggle for individual items or groups.
   *
   * Enforces maxSelections constraint after selection changes.
   */
  const handleToggleCheckbox = useCallback(
    (option: FilterOption) => {
      setTempSelected(prev => {
        let newSelections: FilterOption[];

        // Handle group options
        if (isGroupOption(option)) {
          newSelections = toggleGroupOption(prev, option);
        } else {
          // Handle individual options
          newSelections = toggleIndividualOption(prev, option);
        }

        // Enforce maxSelections constraint
        return enforceMaxSelections(newSelections, maxSelections);
      });
    },
    [maxSelections]
  );

  /**
   * Handle Apply button click
   * Filters out any group items before calling onApply
   */
  const handleApply = useCallback(() => {
    // Filter out any group items before applying
    const filteredSelection = tempSelected.filter(
      item => !(item as GroupOption).isGroup
    );

    onApply(filteredSelection);
    setIsOpen(false);
  }, [tempSelected, onApply, setIsOpen]);

  /**
   * Handle Clear/X button click
   */
  const handleClearClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setTempSelected([]);
      onClear();
    },
    [onClear]
  );

  /**
   * Handle Select All button click
   * Selects all non-disabled, non-group options
   */
  const handleSelectAll = useCallback(() => {
    // Get all selectable options (non-group, non-disabled)
    const allSelectableOptions = enhancedOptions
      .flatMap(option => {
        if ((option as GroupOption).isGroup && (option as GroupOption).items) {
          return (option as GroupOption).items!.filter(item => !item.disabled);
        }
        return option.disabled ? [] : [option];
      })
      .filter(option => !(option as GroupOption).isGroup);

    setTempSelected(allSelectableOptions as FilterOption[]);
  }, [enhancedOptions]);

  /**
   * Handle Deselect All button click
   * Clears all selections
   */
  const handleDeselectAll = useCallback(() => {
    setTempSelected([]);
  }, []);

  /**
   * Handle Enter key press in search input - Power User Keyboard Shortcut
   *
   * When the user types in the search box and filters down to exactly ONE
   * selectable option, pressing Enter will:
   * 1. Toggle that option's selection state (select if unselected, deselect if selected)
   * 2. Automatically apply the change
   * 3. Close the dropdown
   *
   * This provides a fast keyboard-driven workflow for power users who want to
   * quickly filter and select options without reaching for the mouse.
   *
   * Edge cases handled:
   * - Disabled options are excluded from count
   * - Group headers are excluded from count
   * - "Select All" option is excluded from count
   * - If 0 matches or 2+ matches: no action taken
   * - Works for both selecting and deselecting options
   *
   * @param e - Keyboard event from search input
   */
  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;

      e.preventDefault();
      e.stopPropagation();

      // Extract all selectable options (non-group, non-disabled, non-SelectAll)
      const selectableOptions = optionsWithCustom
        .flatMap(option => {
          if (
            (option as GroupOption).isGroup &&
            (option as GroupOption).items
          ) {
            return (option as GroupOption).items || [];
          }
          return [option];
        })
        .filter(opt => {
          // Exclude disabled options
          if (opt.disabled) return false;

          // Exclude groups
          if ((opt as GroupOption).isGroup) return false;

          // Exclude "Select All" option - it's not a real selectable filter value
          // Note: "Select All" is not in filteredOptions since it's added by DropdownList,
          // but we check just to be safe
          if (opt.value === '__select_all__') return false;
          if (opt.label === 'Select All') return false;

          return true;
        });

      // Only proceed if exactly ONE selectable option
      if (selectableOptions.length !== 1) return;

      const singleOption = selectableOptions[0];

      // Toggle selection state
      const isCurrentlySelected = tempSelected.some(
        item => item.value === singleOption.value
      );

      const newSelection = isCurrentlySelected
        ? tempSelected.filter(item => item.value !== singleOption.value)
        : [...tempSelected, singleOption];

      // Update temp selection and apply immediately
      setTempSelected(newSelection);

      // Filter out any group items before applying (same as handleApply)
      const filteredSelection = newSelection.filter(
        item => !(item as GroupOption).isGroup
      );

      onApply(filteredSelection);
      setIsOpen(false);
    },
    [optionsWithCustom, tempSelected, onApply, setIsOpen]
  );

  /**
   * Compute collapsed count and labels for display.
   * Fully-selected groups count as 1 and show group name.
   */
  const { collapsedCount, collapsedLabels } = useMemo(() => {
    if (selected.length === 0)
      return { collapsedCount: 0, collapsedLabels: [] as string[] };

    const itemsInFullGroups = new Set<string>();
    const fullGroupLabels: string[] = [];

    for (const option of enhancedOptions) {
      if (isGroupOption(option)) {
        const groupItems = (option.groupItems ?? option.items) as
          | FilterOption[]
          | undefined;
        if (!groupItems || groupItems.length === 0) continue;

        const allSelected = groupItems.every(item =>
          selected.some(s => s.id === item.id)
        );
        if (allSelected) {
          fullGroupLabels.push(option.label);
          groupItems.forEach(item =>
            itemsInFullGroups.add(item.id?.toString() ?? '')
          );
        }
      }
    }

    const individualItems = selected.filter(
      item =>
        !(item as GroupOption).isGroup &&
        !itemsInFullGroups.has(item.id?.toString() ?? '')
    );

    return {
      collapsedCount: fullGroupLabels.length + individualItems.length,
      collapsedLabels: [
        ...fullGroupLabels,
        ...individualItems.map(item => item.label),
      ],
    };
  }, [selected, enhancedOptions]);

  const renderSelectedContent = () => {
    if (collapsedCount === 0) return null;

    const selectedText = collapsedCount.toString();

    const tooltipContent = (
      <div className="w-full min-w-[200px]">
        {isNegated && (
          <div className="mb-1 text-xs font-semibold uppercase">Excluding:</div>
        )}
        <ul className="space-y-1">
          {collapsedLabels.map((label, index) => (
            <li key={index} className="text-sm">
              {label}
            </li>
          ))}
        </ul>
      </div>
    );

    return (
      <>
        <span className="text-chariot-brand-primary mx-2">|</span>
        <Tooltip title={tooltipContent} placement="bottom">
          <span className="bg-chariot-brand-primary inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] leading-none font-bold text-white">
            {selectedText}
          </span>
        </Tooltip>
        <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 text-current" />
      </>
    );
  };

  /**
   * Process options to add checked state for DropdownList
   */
  const processedOptions = useMemo(() => {
    return optionsWithCustom.map(option => {
      if ((option as GroupOption).isGroup && (option as GroupOption).items) {
        const groupOption = option as GroupOption;
        // For groups, check if any items are selected
        const hasSelectedItems = groupOption.items!.some(item =>
          tempSelected.some(s => s.id === item.id)
        );

        return {
          ...groupOption,
          checked: hasSelectedItems,
          items: groupOption.items!.map(item => ({
            ...item,
            checked: tempSelected.some(s => s.id === item.id),
          })),
        };
      }

      return {
        ...option,
        checked: tempSelected.some(s => s.id === option.id),
      };
    });
  }, [optionsWithCustom, tempSelected]);

  const active = selected.length > 0;

  return (
    <>
      <div className="relative">
        {isLoading && (
          <Skeleton
            shape="block"
            className="absolute top-0 left-0 z-10 size-full rounded-2xl"
          />
        )}

        {/* Trigger Button */}
        <button
          ref={buttonRef}
          onClick={e => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          data-testid={`filter-dropdown-${id}`}
          aria-label={
            active
              ? isNegated
                ? `NOT ${label}: ${collapsedCount} excluded. ${collapsedLabels.join(', ')}`
                : `${label}: ${collapsedCount} selected. ${collapsedLabels.join(', ')}`
              : label
          }
          className={twMerge(
            'flex max-w-full min-w-0 items-center rounded-full border px-2 py-1 text-xs font-bold transition-colors duration-150',
            active
              ? isNegated
                ? 'border-chariot-brand-primary bg-chariot-background-secondary text-chariot-text-primary border-dashed'
                : 'border-chariot-brand-primary bg-chariot-background-secondary text-chariot-text-primary'
              : 'border-chariot-stroke text-chariot-text-secondary border-dashed'
          )}
        >
          {active ? (
            isNegated ? (
              <NoSymbolIcon
                className="text-chariot-text-primary mr-2 h-5 w-5 shrink-0"
                onClick={handleClearClick}
              />
            ) : (
              <XCircleIcon
                className="text-chariot-text-primary mr-2 h-5 w-5 shrink-0"
                onClick={handleClearClick}
              />
            )
          ) : (
            <PlusCircleIcon className="text-chariot-text-secondary mr-2 h-5 w-5 shrink-0" />
          )}
          <span className="truncate">
            {active && isNegated ? `NOT ${label}` : label}
          </span>
          {renderSelectedContent()}
        </button>
      </div>

      {/* Portal Dropdown — rendered into document.body so `position: fixed`
          descendants are anchored to the viewport, not the nearest transformed
          ancestor. This keeps the panel correctly positioned when
          FilterDropdown is used inside a container that applies a CSS
          `transform` (e.g. a shadcn Dialog centered via `-translate-x-1/2
          -translate-y-1/2`). Click-outside still works through the portal
          because React events bubble via the component tree, not the DOM.

          ATTRIBUTE CONTRACT: every portalled node carries
          `data-filter-dropdown-portal`. Radix Dialog's DismissableLayer detects
          `pointerDownOutside` via `event.target instanceof Element && !layer.
          contains(event.target)`. Because our portal is a sibling of the
          DialogContent in the DOM (both children of body), clicks inside this
          panel are "outside" the dialog and would dismiss the whole wizard.
          Any Dialog host wanting to keep the dialog open when a user clicks
          inside a nested FilterDropdown must guard `onInteractOutside` with:
              const target = event.target as HTMLElement | null;
              if (target?.closest('[data-filter-dropdown-portal]')) {
                event.preventDefault();
              }
          Do NOT rely on stopPropagation — Radix listens in the capture phase
          at the document level. The attribute + closest() check is the
          idiomatic escape hatch. */}
      {isOpen &&
        dropdownStyle &&
        createPortal(
          <div
            data-filter-dropdown-portal
            className="fixed top-0 left-0 z-9999 h-0 w-full overflow-visible"
            style={{ pointerEvents: 'none' }}
          >
            {/* Click-outside overlay */}
            <div
              data-filter-dropdown-portal
              className="fixed inset-0 z-40"
              style={{ pointerEvents: 'auto' }}
              onClick={() => {
                setIsOpen(false);
              }}
            />
            {/* Dropdown content */}
            <div
              ref={dropdownRef}
              data-filter-dropdown-portal
              className="absolute z-50"
              style={{
                top: dropdownStyle?.top,
                left: dropdownStyle?.left,
                pointerEvents: dropdownStyle?.pointerEvents,
              }}
            >
              {onToggleNegation && (
                <div
                  className="border-chariot-brand-primary bg-chariot-background-primary mt-2 flex items-center gap-2 rounded-t border border-b-0 px-3 py-2"
                  style={{ width: `${width}px` }}
                >
                  <div className="bg-chariot-background-secondary flex rounded-full p-0.5">
                    <button
                      type="button"
                      onClick={() => {
                        if (isNegated) onToggleNegation();
                      }}
                      className={twMerge(
                        'rounded-full px-3 py-0.5 text-xs font-medium transition-colors',
                        !isNegated
                          ? 'bg-chariot-brand-primary text-white'
                          : 'text-chariot-text-secondary hover:text-chariot-text-primary'
                      )}
                    >
                      Include
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!isNegated) onToggleNegation();
                      }}
                      className={twMerge(
                        'rounded-full px-3 py-0.5 text-xs font-medium transition-colors',
                        isNegated
                          ? 'bg-chariot-brand-primary text-white'
                          : 'text-chariot-text-secondary hover:text-chariot-text-primary'
                      )}
                    >
                      Exclude
                    </button>
                  </div>
                </div>
              )}
              <DropdownList
                fitWidth={true} // Allows option text to expand fully without truncation
                options={processedOptions}
                onToggleCheckbox={handleToggleCheckbox}
                className={twMerge(
                  'bg-chariot-background-primary border-chariot-brand-primary border shadow-lg',
                  onToggleNegation ? 'rounded-b' : 'mt-2 rounded'
                )}
                style={{ width: `${width}px` }} // CRITICAL: Inline style overrides Tailwind w-[280px]/w-[400px] classes
                title={`Select ${label}`}
                searchValue={showSearch ? searchValue : undefined}
                onSearchChange={showSearch ? setSearchValue : undefined}
                onSearchKeyDown={showSearch ? handleSearchKeyDown : undefined}
                searchPlaceholder="Search ..."
                multiSelect={true}
                onApply={handleApply}
                virtualized={optionsWithCustom.length > 10}
                isPraetorianUser={isPraetorianUser}
                isLoadingOptions={isLoadingOptions}
                onSelectAll={
                  !showSelectAll || maxSelections === 1
                    ? undefined
                    : handleSelectAll
                }
                onDeselectAll={showSelectAll ? handleDeselectAll : undefined}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
